// 배경 테마 picker — Palette 아이콘 + popup. 톱니바퀴 옆에 별도 mount.
// Portal 로 body 에 직접 popup mount → sidebar overflow:hidden 무관, 잘림 없음.
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Palette } from "lucide-react";
import {
  useTheme,
  THEME_OPTIONS,
  ACCENT_OPTIONS,
  isLightEnough,
} from "../audit/useTheme";

export default function ThemePicker({ collapsed }) {
  const {
    theme,
    setTheme,
    customColor,
    setCustomColor,
    accent,
    setAccent,
    accentCustom,
    setAccentCustom,
  } = useTheme();
  const [open, setOpen] = useState(false);
  const [customWarn, setCustomWarn] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // open 시 button 위치 → popup 좌표 계산. button 오른쪽 + 같은 top 으로.
  // viewport 우측 넘으면 button 왼쪽으로.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const POPUP_W = 240;
    const POPUP_H = 420; // 배경 5 + 강조 2 옵션 + section 헤더
    let left = r.right + 8;
    if (left + POPUP_W > window.innerWidth - 8) {
      left = r.left - POPUP_W - 8;
    }
    let top = r.top;
    // 아래로 나가면 위로 올림 — sidebar bottom 톱니바퀴는 보통 화면 하단 → 위로 열림
    if (top + POPUP_H > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - POPUP_H - 8);
    }
    setPos({ left, top });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const btnCls = collapsed
    ? "w-7 h-7 rounded flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
    : "text-gray-400 hover:text-gray-700 flex-shrink-0 transition-colors";

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={btnCls}
        title="배경 테마"
      >
        <Palette size={14} />
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              zIndex: 10000,
              minWidth: 240,
              maxHeight: "85vh",
              overflowY: "auto",
              padding: 8,
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              fontFamily: "inherit",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "4px 6px",
              }}
            >
              배경 테마
            </div>
            {THEME_OPTIONS.map((opt) => {
              const active = theme === opt.id;
              const isCustom = opt.id === "custom";
              const swatchColor = isCustom ? customColor : opt.swatch;
              return (
                <div
                  key={opt.id}
                  onClick={() => setTheme(opt.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    border: "1px solid " + (active ? "#374151" : "transparent"),
                    background: active ? "#f3f4f6" : "transparent",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12,
                    color: "#374151",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!active)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {isCustom ? (
                    <label
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        background: swatchColor,
                        border: "1px solid #d1d5db",
                        flexShrink: 0,
                        cursor: "pointer",
                      }}
                      title="색 선택"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="color"
                        value={swatchColor}
                        onInput={(e) => {
                          const ok = setCustomColor(e.target.value);
                          if (!ok) {
                            setCustomWarn(true);
                            setTimeout(() => setCustomWarn(false), 2500);
                          } else {
                            setCustomWarn(false);
                          }
                        }}
                        onChange={(e) => {
                          const ok = setCustomColor(e.target.value);
                          if (!ok) {
                            setCustomWarn(true);
                            setTimeout(() => setCustomWarn(false), 2500);
                          } else {
                            setCustomWarn(false);
                          }
                        }}
                        style={{
                          position: "absolute",
                          inset: 0,
                          opacity: 0,
                          cursor: "pointer",
                          width: "100%",
                          height: "100%",
                          padding: 0,
                          border: "none",
                        }}
                      />
                    </label>
                  ) : (
                    <span
                      style={{
                        display: "inline-block",
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        background: swatchColor,
                        border: "1px solid #d1d5db",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{opt.label}</span>
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>
                      {isCustom && customWarn
                        ? "⚠ 너무 어두운 색 — 검정 텍스트 대비 부족"
                        : opt.desc}
                    </span>
                  </span>
                </div>
              );
            })}
            {/* ─── 강조 색 (active row / selected chip 배경) ─── */}
            <div
              style={{
                borderTop: "1px solid #e5e7eb",
                margin: "6px 0 2px",
              }}
            />
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                padding: "4px 6px",
              }}
            >
              강조 색 (선택 row / chip 배경)
            </div>
            {ACCENT_OPTIONS.map((opt) => {
              const active = accent === opt.id;
              const isCustom = opt.id === "custom";
              const swatchColor = isCustom ? accentCustom : opt.bg;
              return (
                <div
                  key={opt.id}
                  onClick={() => setAccent(opt.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    border: "1px solid " + (active ? "#374151" : "transparent"),
                    background: active ? "#f3f4f6" : "transparent",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12,
                    color: "#374151",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!active)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {isCustom ? (
                    <label
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        background: swatchColor,
                        border: "1px solid #d1d5db",
                        flexShrink: 0,
                        cursor: "pointer",
                      }}
                      title="강조 색 선택"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="color"
                        value={swatchColor}
                        onInput={(e) => setAccentCustom(e.target.value)}
                        onChange={(e) => setAccentCustom(e.target.value)}
                        style={{
                          position: "absolute",
                          inset: 0,
                          opacity: 0,
                          cursor: "pointer",
                          width: "100%",
                          height: "100%",
                          padding: 0,
                          border: "none",
                        }}
                      />
                    </label>
                  ) : (
                    <span
                      style={{
                        display: "inline-block",
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        background: swatchColor,
                        border: "1px solid #d1d5db",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{opt.label}</span>
                    <span style={{ fontSize: 10, color: "#9ca3af" }}>
                      {opt.desc}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
